import React, { useMemo, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function formatCountdown(ms) {
  if (!Number.isFinite(ms)) return '--';
  return Math.max(0, Math.ceil(ms / 1000));
}

function ShapePreview({ shape, accent }) {
  const color = accent || '#3b82f6';

  return (
    <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <svg viewBox="-1.2 -1.2 2.4 2.4" className="h-14 w-14">
        {shape?.key === 'circle' ? (
          <circle cx="0" cy="0" r="0.9" fill="none" stroke={color} strokeWidth="0.12" />
        ) : shape?.key === 'diamond' ? (
          <polygon points="0,-1 1,0 0,1 -1,0" fill="none" stroke={color} strokeWidth="0.12" />
        ) : shape?.key === 'star' ? (
          <polygon points="0,-1 0.24,-0.24 1,0 0.24,0.24 0,1 -0.24,0.24 -1,0 -0.24,-0.24" fill="none" stroke={color} strokeWidth="0.12" />
        ) : shape?.key === 'hexagon' ? (
          <polygon points="0.9,0.5 0.9,-0.5 0, -1 -0.9,-0.5 -0.9,0.5 0,1" fill="none" stroke={color} strokeWidth="0.12" />
        ) : shape?.key === 'square' ? (
          <rect x="-0.8" y="-0.8" width="1.6" height="1.6" rx="0.12" fill="none" stroke={color} strokeWidth="0.12" />
        ) : (
          <polygon points="0,-1 1,0.5 -0.6,0.5 -0.6,-0.5 1,-0.5" fill="none" stroke={color} strokeWidth="0.12" />
        )}
      </svg>
    </div>
  );
}

export default function ShapeShifterGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [feedback, setFeedback] = useState('');

  const currentRound = gameState?.currentRound || {};
  const finished = !!gameState?.finished;
  const remainingMs = gameState?.roundEndsAt ? Math.max(0, gameState.roundEndsAt - Date.now()) : null;
  const leaderboard = useMemo(() => {
    return Array.isArray(gameState?.rankings) && gameState.rankings.length > 0
      ? gameState.rankings
      : (gameState.players || []).map((player) => ({ socketId: player.socketId, username: player.username || player.socketId, score: player.score || 0 }));
  }, [gameState?.rankings, gameState?.players]);

  const dispatchAction = async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  };

  const handleSelectShape = async (shapeKey) => {
    await dispatchAction({ type: 'select-shape', shapeKey });
    setFeedback(shapeKey === currentRound?.promptShape?.key ? 'Correct!' : 'Wrong — you were eliminated.');
  };

  return (
    <div className="w-full rounded-[28px] border border-slate-200 bg-white p-4 text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
      <div className="mb-4 flex w-full flex-col gap-3">
        <div className="w-full">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">ShapeShifter</div>
          <div className="text-lg font-semibold">Spot the previous shape before it shifts.</div>
          <div className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
            A shape appears, then shifts. Tap the shape you saw before it changed.
          </div>
        </div>
        <div className="flex w-full flex-wrap gap-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Round</div>
            <div className="font-semibold">{gameState?.activeRoundNumber || 1}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Time</div>
            <div className="font-semibold">{formatCountdown(remainingMs)}s</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Phase</div>
            <div className="font-semibold">{currentRound?.phase === 'countdown' ? `Start ${gameState?.countdownValue || 3}` : currentRound?.phase === 'answering' ? 'Tap now' : 'Watch'}</div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-cyan-50 p-4 dark:border-slate-700 dark:from-slate-950 dark:to-cyan-950">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Current prompt</div>
            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {currentRound?.phase === 'countdown' ? `Starting in ${gameState?.countdownValue || 3}` : currentRound?.phase === 'answering' ? 'It shifted — tap the earlier shape' : 'Watch the shape carefully'}
            </div>
          </div>
          <div className="rounded-full bg-white/80 px-3 py-1 text-sm font-medium text-slate-700 shadow-sm dark:bg-slate-800/80 dark:text-slate-200">
            {currentRound?.phase === 'answering' ? 'Answer now' : 'Pending'}
          </div>
        </div>

        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Shown shape</div>
            <div className="mt-2 flex items-center gap-3">
              <ShapePreview shape={currentRound?.displayShape || currentRound?.shiftedShape || currentRound?.promptShape} accent="#8b5cf6" />
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{(currentRound?.displayShape || currentRound?.shiftedShape || currentRound?.promptShape)?.label || '—'}</div>
                <div className="text-xs text-slate-600 dark:text-slate-300">{currentRound?.phase === 'answering' ? 'This is the shape after the shift.' : 'This is the shape you need to remember.'}</div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Your choice</div>
            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Pick the earlier shape from the grid below. One wrong tap ends the run.
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(currentRound?.options || []).map((shape) => (
            <button
              key={shape.key}
              type="button"
              onClick={() => handleSelectShape(shape.key)}
              className="rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex items-center gap-3">
                <ShapePreview shape={shape} accent="#2563eb" />
                <div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{shape.label}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">Tap to choose</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-4 text-sm text-slate-600 dark:text-slate-300">{feedback || 'Watch closely and react fast.'}</div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Leaderboard</div>
          <div className="mt-2 space-y-2">
            {leaderboard.map((row, index) => (
              <div key={row.socketId} className="flex w-full items-center justify-between rounded-2xl bg-white px-3 py-2 text-sm shadow-sm dark:bg-slate-900">
                <div className="font-medium">#{index + 1} {row.username || row.socketId}</div>
                <div className="text-slate-600 dark:text-slate-300">{row.score}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/70">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">How to play</div>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600 dark:text-slate-300">
            <li>The prompt appears briefly, then shifts to a new shape.</li>
            <li>Tap the earlier shape from the grid to score a point.</li>
            <li>Wrong taps eliminate you and the last surviving player wins.</li>
          </ul>
        </div>
      </div>

      {finished && (
        <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
          Round complete. The player with the most correct answers wins.
        </div>
      )}
    </div>
  );
}
